import { useCallback, useEffect, useState } from 'react';
import { isElectronServerRuntime, getServerLicense, activateServerLicense } from '@/lib/electronBridge';

/**
 * Ask the Institute EXE for its activation key, once, before it is used.
 *
 * The main process has had get / validate / clear handlers for a server
 * licence all along, the preload exposes them, and a superadmin can issue a
 * key per institute. Nothing in the interface ever called any of it -- so the
 * EXE never asked, and every copy ran unlicensed. The whole licensing
 * mechanism existed except the part that makes it happen.
 *
 * This is deliberately a GATE and not a settings page: the key identifies
 * which institute this server belongs to, and the relay, the sync bundle and
 * the lab clients all hang off that answer. Asking for it afterwards would
 * mean running as nobody until somebody remembered.
 *
 * It only ever appears in the Institute EXE. In a browser there is no server
 * to license and `isElectronServerRuntime()` is false, so this renders
 * nothing at all.
 */
export default function ServerLicenseGate({ children }: { children: React.ReactNode }) {
    // null = not asked yet; the gate must not flash before the answer is known.
    const [licensed, setLicensed] = useState<boolean | null>(null);
    const [key, setKey] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [activated, setActivated] = useState<any>(null);
    // "Running, but no key" -- shown as a banner rather than a barrier.
    const [needsKey, setNeedsKey] = useState(false);
    const [prompting, setPrompting] = useState(false);
    // Set when the cloud has said this server's account is gone, so the
    // operator is told WHY they are being asked for a key again.
    const [revokedReason, setRevokedReason] = useState('');

    useEffect(() => {
        if (!isElectronServerRuntime()) { setLicensed(true); return; }
        let cancelled = false;
        (async () => {
            const current = await getServerLicense();
            if (cancelled) return;
            // Three ways to be let straight through, and each matters:
            //  - a build too old to answer (current === null): it cannot be
            //    asked, and must not lock the institute out of its own server;
            //  - a key already stored: activated, nothing to do;
            //  - a server ALREADY IN SERVICE: keys were never requested until
            //    now, so working institutes have none. Demanding one on an
            //    update would take a running lab offline, and at least one
            //    institute has no key issued to type. Fresh installs only.
            const hasKey = Boolean(current?.key);
            const inService = Boolean((current as any)?.configured);
            // A fourth case, and it overrides the other three: the cloud has
            // told this server its account no longer exists. That happens when
            // the institute it was activated against is deleted, and the stored
            // key is then a key to nothing. Left to the rules above, the key
            // alone counted as "activated", so the server showed a deactivation
            // notice with no way to re-link it and quietly synced nothing.
            const revoked = Boolean((current as any)?.revoked);
            setRevokedReason(revoked ? String((current as any)?.revokedReason || '') : '');
            setLicensed(current === null ? true : revoked ? false : hasKey || inService);
            setNeedsKey(current !== null && !revoked && !hasKey && inService);
        })();
        return () => { cancelled = true; };
    }, []);

    const activate = useCallback(async () => {
        const trimmed = key.trim();
        if (!trimmed) { setError('Enter the activation key given to this institute.'); return; }
        setBusy(true);
        setError('');
        const res = await activateServerLicense(trimmed);
        if (res?.success) {
            setActivated(res.data || {});
            // Let the operator see WHICH institute they just activated before
            // the portal replaces this screen -- a key typed into the wrong
            // server is otherwise discovered much later.
            setTimeout(() => { setLicensed(true); setNeedsKey(false); setPrompting(false); }, 1600);
        } else {
            setError(res?.error || 'That key was not accepted.');
        }
        setBusy(false);
    }, [key]);

    // One activation form, used by the blocking screen and by the banner's
    // dialog. Two copies of this would drift the moment either changed.
    const panel = (
        <>
            <h1 className="text-lg font-bold text-slate-900 dark:text-slate-100">Activate this server</h1>
                {revokedReason && (
                    // Without this the screen looks like a first-run prompt on a
                    // server that has been working for months, and the operator
                    // has no idea why it is suddenly asking.
                    <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100">
                        <p className="font-semibold">This server needs to be linked again</p>
                        <p className="mt-0.5">{revokedReason}</p>
                        <p className="mt-1 text-xs">
                            Nothing stored on this computer has been deleted. Enter the new
                            activation key for your institute to carry on.
                        </p>
                    </div>
                )}
                <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                    Enter the activation key issued to your institute. It tells this server which
                    institute it belongs to, so it can sync and serve the lab computers.
                </p>

                {activated ? (
                    <div className="mt-5 rounded-lg border border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-900 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-100">
                        <p className="font-semibold">Activated</p>
                        <p className="mt-0.5">
                            {activated.institute_name || 'This institute'}
                            {activated.institute_code ? ` (${activated.institute_code})` : ''}
                        </p>
                    </div>
                ) : (
                    <>
                        <input
                            value={key}
                            onChange={(e) => { setKey(e.target.value); setError(''); }}
                            onKeyDown={(e) => { if (e.key === 'Enter') void activate(); }}
                            placeholder="Activation key"
                            autoFocus
                            spellCheck={false}
                            className="mt-5 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm tracking-wide focus:border-primary-500 focus:outline-none dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                        />
                        {error && <p className="mt-2 text-sm font-medium text-red-600 dark:text-red-400">{error}</p>}
                        <button
                            type="button"
                            onClick={activate}
                            disabled={busy}
                            className="mt-4 w-full rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-60"
                        >
                            {busy ? 'Checking…' : 'Activate'}
                        </button>
                        <p className="mt-4 text-xs text-slate-500 dark:text-slate-400">
                            The key is checked with eSwift, so this computer needs internet access
                            once, now. After that the server runs offline.
                        </p>
                    </>
                )}
        </>
    );

    if (licensed === null) return null;
    // Already in service but holding no key: it keeps working, and says so.
    // Blocking here would take a running lab offline over a key nobody at the
    // institute may have to hand -- but leaving it silent is how a server ends
    // up unlicensed for months, so it is visible and one click from fixed.
    if (licensed && needsKey) {
        return (
            <>
                {prompting ? (
                    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-6"
                         onClick={(e) => { if (e.target === e.currentTarget) setPrompting(false); }}>
                        <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl dark:bg-slate-800">
                            {panel}
                            <button type="button" onClick={() => setPrompting(false)}
                                className="mt-3 w-full rounded-lg border border-slate-300 px-4 py-2 text-sm dark:border-slate-600 dark:text-slate-200">
                                Not now
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-wrap items-center gap-3 bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:bg-amber-500/10 dark:text-amber-100">
                        <span className="flex-1 min-w-[240px]">
                            This server has not been activated with its licence key.
                        </span>
                        <button type="button" onClick={() => setPrompting(true)}
                            className="rounded-md bg-amber-600 px-3 py-1 text-xs font-semibold text-white hover:bg-amber-700">
                            Activate
                        </button>
                    </div>
                )}
                {children}
            </>
        );
    }
    if (licensed) return <>{children}</>;

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-100 p-6 dark:bg-slate-900">
            <div className="w-full max-w-md rounded-2xl bg-white p-7 shadow-lg dark:bg-slate-800">
                {panel}
            </div>
        </div>
    );
}
